import {
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded"
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded"
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded"
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded"

import MainLayout from "../../layouts/MainLayout"

import {
  createStockTransfer,
  getStockTransferById,
  getStockTransferFormData,
  getStockTransfers,
  updateStockTransfer,
} from "../../services/stockTransferService"


// =========================================================
// CONSTANTS
// =========================================================

const ROWS_PER_PAGE = 10


const defaultFormData = {
  company_id: 1,
  product_id: "",
  source_type: "warehouse",
  destination_type: "store",
  from_store_id: "",
  from_warehouse_id: "",
  to_store_id: "",
  to_warehouse_id: "",
  quantity: "",
  status: "approved",
  reference_note: "",
  created_by: 1,
  approved_by: 1,
}


// =========================================================
// HELPERS
// =========================================================

function toNumber(value) {
  const number =
    Number(value || 0)

  return Number.isFinite(number)
    ? number
    : 0
}


function formatQuantity(value) {
  return toNumber(value).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }
  )
}


function formatDateTime(value) {
  if (!value) {
    return "-"
  }

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "-"
  }

  return date.toLocaleString(
    "fr-FR",
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  )
}


function getStatusConfig(value) {
  const normalized =
    String(
      value || ""
    ).toLowerCase()

  if (
    normalized === "approved"
  ) {
    return {
      label: "Approuvé",
      color: "success",
    }
  }

  if (
    normalized === "pending"
  ) {
    return {
      label: "En attente",
      color: "warning",
    }
  }

  if (
    normalized === "cancelled" ||
    normalized === "canceled"
  ) {
    return {
      label: "Annulé",
      color: "error",
    }
  }

  if (
    normalized === "completed"
  ) {
    return {
      label: "Terminé",
      color: "info",
    }
  }

  return {
    label:
      value || "-",
    color: "default",
  }
}


function getStatusChip(value) {
  const config =
    getStatusConfig(value)

  return (
    <Chip
      size="small"
      color={config.color}
      label={config.label}
      sx={{
        height: 24,
        fontWeight: 900,
      }}
    />
  )
}


function getSourceType(row) {
  if (
    row?.from_store_id
  ) {
    return "store"
  }

  if (
    row?.from_warehouse_id
  ) {
    return "warehouse"
  }

  return String(
    row?.source_type ||
      ""
  ).toLowerCase()
}


function getDestinationType(row) {
  if (
    row?.to_store_id
  ) {
    return "store"
  }

  if (
    row?.to_warehouse_id
  ) {
    return "warehouse"
  }

  return String(
    row?.destination_type ||
      ""
  ).toLowerCase()
}


function getLocationTypeLabel(value) {
  if (
    value === "store"
  ) {
    return "Shop"
  }

  if (
    value === "warehouse"
  ) {
    return "Entrepôt"
  }

  return "-"
}


function getLocationTypeChip(
  value,
  variant = "outlined"
) {
  if (
    value === "store"
  ) {
    return (
      <Chip
        size="small"
        label="Shop"
        color="primary"
        variant={variant}
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  if (
    value === "warehouse"
  ) {
    return (
      <Chip
        size="small"
        label="Entrepôt"
        color="secondary"
        variant={variant}
        sx={{
          height: 24,
          fontWeight: 800,
        }}
      />
    )
  }

  return (
    <Chip
      size="small"
      label="-"
      variant="outlined"
      sx={{
        height: 24,
      }}
    />
  )
}


function getTransferRowClass(status) {
  const normalized =
    String(
      status || ""
    ).toLowerCase()

  if (
    normalized === "approved"
  ) {
    return "stock-transfer-row stock-transfer-row--approved"
  }

  if (
    normalized === "pending"
  ) {
    return "stock-transfer-row stock-transfer-row--pending"
  }

  if (
    normalized === "cancelled" ||
    normalized === "canceled"
  ) {
    return "stock-transfer-row stock-transfer-row--cancelled"
  }

  return "stock-transfer-row stock-transfer-row--default"
}


function getErrorMessage(
  error,
  fallback
) {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail === "string"
  ) {
    return detail
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          JSON.stringify(item)
      )
      .join(" | ")
  }

  if (
    detail &&
    typeof detail === "object"
  ) {
    return (
      detail.message ||
      JSON.stringify(detail)
    )
  }

  return (
    error?.message ||
    fallback
  )
}


// =========================================================
// KPI CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  icon,
  accent = "#2563eb",
  background = "rgba(37, 99, 235, 0.09)",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        px: {
          xs: 1.35,
          sm: 1.5,
        },

        py: 1.15,

        minWidth: 0,

        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        bgcolor: "#ffffff",

        boxShadow:
          "0 6px 18px rgba(15, 23, 42, 0.035)",
      }}
    >
      <Box
        sx={{
          display: "flex",

          alignItems: "center",

          gap: 1.1,

          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,

            flexShrink: 0,

            display: "grid",
            placeItems: "center",

            borderRadius: 2.5,

            color: accent,

            bgcolor: background,
          }}
        >
          {icon}
        </Box>


        <Box
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",

              alignItems: "baseline",

              gap: 0.7,

              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: {
                  xs: 19,
                  sm: 21,
                },

                lineHeight: 1,

                fontWeight: 900,

                color: "#0f172a",

                whiteSpace: "nowrap",
              }}
            >
              {value}
            </Typography>


            <Typography
              variant="body2"
              sx={{
                minWidth: 0,

                fontWeight: 800,

                color: "#334155",

                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          </Box>


          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",

              mt: 0.35,

              lineHeight: 1.25,

              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {caption}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}


// =========================================================
// MOBILE CARD
// =========================================================

function TransferMobileCard({
  row,
  onEdit,
}) {
  const sourceType =
    getSourceType(row)

  const destinationType =
    getDestinationType(row)

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,

        borderColor:
          "rgba(148, 163, 184, 0.22)",

        boxShadow:
          String(
            row.status || ""
          ).toLowerCase() ===
          "pending"
            ? "inset 4px 0 0 #f59e0b, 0 5px 16px rgba(15, 23, 42, 0.025)"
            : "inset 4px 0 0 #22c55e, 0 5px 16px rgba(15, 23, 42, 0.025)",
      }}
    >
      <CardContent
        sx={{
          p: 1.5,

          "&:last-child": {
            pb: 1.5,
          },
        }}
      >
        <Stack spacing={1.25}>
          <Box
            sx={{
              display: "flex",

              justifyContent:
                "space-between",

              alignItems:
                "flex-start",

              gap: 1,
            }}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  overflowWrap:
                    "anywhere",
                }}
              >
                {row.product_name ||
                  "-"}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                {row.product_code ||
                  "-"}
                {row.transfer_number
                  ? ` • ${row.transfer_number}`
                  : ""}
              </Typography>
            </Box>

            {getStatusChip(
              row.status
            )}
          </Box>


          <Paper
            variant="outlined"
            sx={{
              p: 1,

              borderRadius: 2.5,

              bgcolor: "#fbfdff",
            }}
          >
            <Box
              sx={{
                display: "grid",

                gridTemplateColumns:
                  "1fr auto 1fr",

                gap: 0.8,

                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  Source
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 0.6,
                    alignItems: "center",
                    mt: 0.25,
                    minWidth: 0,
                  }}
                >
                  {getLocationTypeChip(
                    sourceType
                  )}

                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{
                      minWidth: 0,
                      overflowWrap:
                        "anywhere",
                    }}
                  >
                    {row.source_location_name ||
                      "-"}
                  </Typography>
                </Box>
              </Box>


              <SwapHorizRoundedIcon
                sx={{
                  color: "#94a3b8",
                }}
              />


              <Box
                sx={{
                  minWidth: 0,
                  textAlign: "right",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={700}
                >
                  Destination
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    gap: 0.6,
                    alignItems: "center",
                    justifyContent:
                      "flex-end",
                    mt: 0.25,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight={800}
                    sx={{
                      minWidth: 0,
                      overflowWrap:
                        "anywhere",
                    }}
                  >
                    {row.destination_location_name ||
                      "-"}
                  </Typography>

                  {getLocationTypeChip(
                    destinationType
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns:
                "repeat(2, minmax(0, 1fr))",

              gap: 0.8,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Quantité
              </Typography>

              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                {formatQuantity(
                  row.quantity
                )}
              </Typography>
            </Paper>


            <Paper
              variant="outlined"
              sx={{
                p: 0.9,

                borderRadius: 2.5,

                bgcolor: "#f8fafc",
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Date
              </Typography>

              <Typography
                variant="body2"
                fontWeight={800}
                color="#334155"
              >
                {formatDateTime(
                  row.transfer_date
                )}
              </Typography>
            </Paper>
          </Box>


          {row.reference_note ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflowWrap:
                  "anywhere",
              }}
            >
              {row.reference_note}
            </Typography>
          ) : null}


          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={() =>
              onEdit(
                row.id
              )
            }
          >
            Modifier
          </Button>
        </Stack>
      </CardContent>
    </Card>
  )
}


// =========================================================
// PAGE
// =========================================================

function StockTransfersPage() {
  const theme =
    useTheme()

  const isMdDown =
    useMediaQuery(
      theme.breakpoints.down("md")
    )

  const isSmDown =
    useMediaQuery(
      theme.breakpoints.down("sm")
    )


  // =======================================================
  // DATA
  // =======================================================

  const [
    transfers,
    setTransfers,
  ] = useState([])

  const [
    products,
    setProducts,
  ] = useState([])

  const [
    stores,
    setStores,
  ] = useState([])

  const [
    warehouses,
    setWarehouses,
  ] = useState([])


  // =======================================================
  // UI
  // =======================================================

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    selectedTransfer,
    setSelectedTransfer,
  ] = useState(null)

  const [
    formData,
    setFormData,
  ] = useState({
    ...defaultFormData,
  })


  // =======================================================
  // FILTERS
  // =======================================================

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("")


  // =======================================================
  // PAGINATION
  // =======================================================

  const [
    paginationModel,
    setPaginationModel,
  ] = useState({
    page: 0,
    pageSize:
      ROWS_PER_PAGE,
  })


  // =======================================================
  // SNACKBAR
  // =======================================================

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message:
        String(
          message || ""
        ),
    })
  }


  const handleCloseSnackbar =
    () => {
      setSnackbar(
        (previous) => ({
          ...previous,
          open: false,
        })
      )
    }


  // =======================================================
  // LOAD TRANSFERS
  // =======================================================

  const loadTransfers =
    async () => {
      try {
        setLoading(true)

        const data =
          await getStockTransfers()

        setTransfers(
          Array.isArray(data)
            ? data
            : []
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les transferts de stock."
          ),
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  // =======================================================
  // LOAD FORM DATA
  // =======================================================

  const loadFormData =
    async () => {
      try {
        const data =
          await getStockTransferFormData()

        setProducts(
          Array.isArray(
            data?.products
          )
            ? data.products
            : []
        )

        setStores(
          Array.isArray(
            data?.stores
          )
            ? data.stores
            : []
        )

        setWarehouses(
          Array.isArray(
            data?.warehouses
          )
            ? data.warehouses
            : []
        )
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger les données du formulaire."
          ),
          "error"
        )
      }
    }


  useEffect(() => {
    loadTransfers()
    loadFormData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // =======================================================
  // FORM
  // =======================================================

  const resetForm = () => {
    setSelectedTransfer(
      null
    )

    setFormData({
      ...defaultFormData,
    })
  }


  const openAddDialog =
    () => {
      resetForm()
      setOpen(true)
    }


  const openEditDialog =
    async (
      transferId
    ) => {
      try {
        const transfer =
          await getStockTransferById(
            transferId
          )

        setSelectedTransfer(
          transfer
        )

        setFormData({
          company_id:
            transfer.company_id ??
            1,

          product_id:
            transfer.product_id ??
            "",

          source_type:
            transfer.from_store_id
              ? "store"
              : "warehouse",

          destination_type:
            transfer.to_store_id
              ? "store"
              : "warehouse",

          from_store_id:
            transfer.from_store_id ??
            "",

          from_warehouse_id:
            transfer.from_warehouse_id ??
            "",

          to_store_id:
            transfer.to_store_id ??
            "",

          to_warehouse_id:
            transfer.to_warehouse_id ??
            "",

          quantity:
            transfer.quantity ??
            "",

          status:
            transfer.status ||
            "approved",

          reference_note:
            transfer.reference_note ||
            "",

          created_by:
            transfer.created_by ??
            1,

          approved_by:
            transfer.approved_by ??
            1,
        })

        setOpen(true)
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible de charger le transfert."
          ),
          "error"
        )
      }
    }


  const handleClose = () => {
    if (saving) {
      return
    }

    setOpen(false)
    resetForm()
  }


  const handleChange =
    (event) => {
      const {
        name,
        value,
      } =
        event.target

      setFormData(
        (previous) => {
          const next = {
            ...previous,
            [name]: value,
          }

          if (
            name ===
            "source_type"
          ) {
            next.from_store_id =
              ""

            next.from_warehouse_id =
              ""
          }

          if (
            name ===
            "destination_type"
          ) {
            next.to_store_id =
              ""

            next.to_warehouse_id =
              ""
          }

          if (
            name ===
            "from_store_id"
          ) {
            next.from_store_id =
              value
                ? Number(value)
                : ""

            next.from_warehouse_id =
              ""
          }

          if (
            name ===
            "from_warehouse_id"
          ) {
            next.from_warehouse_id =
              value
                ? Number(value)
                : ""

            next.from_store_id =
              ""
          }

          if (
            name ===
            "to_store_id"
          ) {
            next.to_store_id =
              value
                ? Number(value)
                : ""

            next.to_warehouse_id =
              ""
          }

          if (
            name ===
            "to_warehouse_id"
          ) {
            next.to_warehouse_id =
              value
                ? Number(value)
                : ""

            next.to_store_id =
              ""
          }

          if (
            name ===
              "product_id" ||
            name ===
              "quantity"
          ) {
            next[name] =
              value === ""
                ? ""
                : Number(value)
          }

          return next
        }
      )
    }


  // =======================================================
  // VALIDATION
  // =======================================================

  const validateForm = () => {
    if (
      !formData.product_id
    ) {
      showSnackbar(
        "Le produit est obligatoire.",
        "warning"
      )

      return false
    }


    if (
      !formData.quantity ||
      Number(
        formData.quantity
      ) <= 0
    ) {
      showSnackbar(
        "La quantité doit être supérieure à 0.",
        "warning"
      )

      return false
    }


    if (
      formData.source_type ===
        "store" &&
      !formData.from_store_id
    ) {
      showSnackbar(
        "Choisissez le shop source.",
        "warning"
      )

      return false
    }


    if (
      formData.source_type ===
        "warehouse" &&
      !formData.from_warehouse_id
    ) {
      showSnackbar(
        "Choisissez l'entrepôt source.",
        "warning"
      )

      return false
    }


    if (
      formData.destination_type ===
        "store" &&
      !formData.to_store_id
    ) {
      showSnackbar(
        "Choisissez le shop destination.",
        "warning"
      )

      return false
    }


    if (
      formData.destination_type ===
        "warehouse" &&
      !formData.to_warehouse_id
    ) {
      showSnackbar(
        "Choisissez l'entrepôt destination.",
        "warning"
      )

      return false
    }


    if (
      formData.source_type ===
        "store" &&
      formData.destination_type ===
        "store" &&
      String(
        formData.from_store_id
      ) ===
        String(
          formData.to_store_id
        )
    ) {
      showSnackbar(
        "Source et destination ne peuvent pas être identiques.",
        "warning"
      )

      return false
    }


    if (
      formData.source_type ===
        "warehouse" &&
      formData.destination_type ===
        "warehouse" &&
      String(
        formData.from_warehouse_id
      ) ===
        String(
          formData.to_warehouse_id
        )
    ) {
      showSnackbar(
        "Source et destination ne peuvent pas être identiques.",
        "warning"
      )

      return false
    }

    return true
  }


  // =======================================================
  // SAVE
  // =======================================================

  const handleSave =
    async () => {
      if (
        !validateForm()
      ) {
        return
      }

      const payload = {
        company_id: 1,

        product_id:
          Number(
            formData.product_id
          ),

        from_store_id:
          formData.source_type ===
            "store"
            ? Number(
                formData.from_store_id
              )
            : null,

        from_warehouse_id:
          formData.source_type ===
            "warehouse"
            ? Number(
                formData.from_warehouse_id
              )
            : null,

        to_store_id:
          formData.destination_type ===
            "store"
            ? Number(
                formData.to_store_id
              )
            : null,

        to_warehouse_id:
          formData.destination_type ===
            "warehouse"
            ? Number(
                formData.to_warehouse_id
              )
            : null,

        quantity:
          Number(
            formData.quantity
          ),

        status:
          formData.status ||
          "approved",

        reference_note:
          formData.reference_note
            ?.trim() ||
          null,

        created_by: 1,

        approved_by: 1,
      }

      try {
        setSaving(true)

        if (
          selectedTransfer?.id
        ) {
          await updateStockTransfer(
            selectedTransfer.id,
            payload
          )

          showSnackbar(
            "Transfert modifié avec succès."
          )
        } else {
          await createStockTransfer(
            payload
          )

          showSnackbar(
            "Transfert créé avec succès."
          )
        }

        /*
         * Fermeture directe :
         * handleClose() ne ferme pas pendant saving === true.
         */
        setOpen(false)
        resetForm()

        await loadTransfers()
      } catch (error) {
        console.error(error)

        showSnackbar(
          getErrorMessage(
            error,
            "Impossible d'enregistrer le transfert de stock."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filteredTransfers =
    useMemo(() => {
      const keyword =
        search
          .trim()
          .toLowerCase()

      return transfers.filter(
        (item) => {
          const matchesSearch =
            keyword === "" ||
            [
              item.transfer_number,
              item.product_code,
              item.product_name,
              item.source_location_name,
              item.destination_location_name,
              getLocationTypeLabel(
                getSourceType(
                  item
                )
              ),
              getLocationTypeLabel(
                getDestinationType(
                  item
                )
              ),
              item.reference_note,
            ].some(
              (value) =>
                String(
                  value || ""
                )
                  .toLowerCase()
                  .includes(
                    keyword
                  )
            )

          const matchesStatus =
            statusFilter === "" ||
            String(
              item.status || ""
            ) ===
              statusFilter

          return (
            matchesSearch &&
            matchesStatus
          )
        }
      )
    }, [
      transfers,
      search,
      statusFilter,
    ])


  // =======================================================
  // STATS
  // =======================================================

  const stats =
    useMemo(() => {
      return filteredTransfers.reduce(
        (
          accumulator,
          item
        ) => {
          accumulator.total +=
            1

          accumulator.quantity +=
            toNumber(
              item.quantity
            )

          if (
            String(
              item.status || ""
            ).toLowerCase() ===
            "approved"
          ) {
            accumulator.approved +=
              1
          }

          if (
            String(
              item.status || ""
            ).toLowerCase() ===
            "pending"
          ) {
            accumulator.pending +=
              1
          }

          return accumulator
        },
        {
          total: 0,
          approved: 0,
          pending: 0,
          quantity: 0,
        }
      )
    }, [
      filteredTransfers,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  useEffect(() => {
    setPaginationModel(
      (previous) => ({
        ...previous,

        page: 0,
        pageSize:
          ROWS_PER_PAGE,
      })
    )
  }, [
    search,
    statusFilter,
  ])


  const paginatedMobileRows =
    useMemo(() => {
      const start =
        paginationModel.page *
        ROWS_PER_PAGE

      return filteredTransfers.slice(
        start,
        start +
          ROWS_PER_PAGE
      )
    }, [
      filteredTransfers,
      paginationModel.page,
    ])


  useEffect(() => {
    const totalPages =
      Math.max(
        1,
        Math.ceil(
          filteredTransfers.length /
            ROWS_PER_PAGE
        )
      )

    if (
      paginationModel.page >=
      totalPages
    ) {
      setPaginationModel(
        (previous) => ({
          ...previous,

          page:
            totalPages - 1,

          pageSize:
            ROWS_PER_PAGE,
        })
      )
    }
  }, [
    filteredTransfers.length,
    paginationModel.page,
  ])


  const resetFilters = () => {
    setSearch("")
    setStatusFilter("")

    setPaginationModel({
      page: 0,
      pageSize:
        ROWS_PER_PAGE,
    })
  }


  // =======================================================
  // COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "transfer",

          headerName:
            "Transfert",

          minWidth: 180,

          flex: 1.05,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.16}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.85rem",
                    fontWeight: 900,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.transfer_number ||
                    ""
                  }
                >
                  {params.row.transfer_number ||
                    `TR-${params.row.id}`}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatDateTime(
                    params.row.transfer_date
                  )}
                </Typography>
              </Stack>
            ),
        },


        {
          field: "product",

          headerName:
            "Produit",

          minWidth: 210,

          flex: 1.2,

          sortable: false,

          renderCell:
            (params) => (
              <Stack
                spacing={0.16}

                justifyContent="center"

                sx={{
                  width: "100%",
                  height: "100%",
                  minWidth: 0,
                  overflow: "hidden",
                }}
              >
                <Typography
                  sx={{
                    color: "#0f172a",
                    fontSize: "0.86rem",
                    fontWeight: 900,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.product_name ||
                    ""
                  }
                >
                  {params.row.product_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "#64748b",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {params.row.product_code ||
                    "-"}
                </Typography>
              </Stack>
            ),
        },


        {
          field: "source",

          headerName:
            "Source",

          minWidth: 210,

          flex: 1.2,

          sortable: false,

          renderCell:
            (params) => (
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,

                  display: "flex",

                  alignItems: "center",

                  gap: 0.8,
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  {getLocationTypeChip(
                    getSourceType(
                      params.row
                    )
                  )}
                </Box>

                <Typography
                  sx={{
                    minWidth: 0,

                    color: "#334155",

                    fontSize: "0.83rem",

                    fontWeight: 800,

                    lineHeight: 1.25,

                    overflow: "hidden",

                    textOverflow:
                      "ellipsis",

                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.source_location_name ||
                    ""
                  }
                >
                  {params.row.source_location_name ||
                    "-"}
                </Typography>
              </Box>
            ),
        },


        {
          field:
            "destination",

          headerName:
            "Destination",

          minWidth: 210,

          flex: 1.2,

          sortable: false,

          renderCell:
            (params) => (
              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,

                  display: "flex",

                  alignItems: "center",

                  gap: 0.8,
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                  }}
                >
                  {getLocationTypeChip(
                    getDestinationType(
                      params.row
                    )
                  )}
                </Box>

                <Typography
                  sx={{
                    minWidth: 0,

                    color: "#334155",

                    fontSize: "0.83rem",

                    fontWeight: 800,

                    lineHeight: 1.25,

                    overflow: "hidden",

                    textOverflow:
                      "ellipsis",

                    whiteSpace: "nowrap",
                  }}

                  title={
                    params.row.destination_location_name ||
                    ""
                  }
                >
                  {params.row.destination_location_name ||
                    "-"}
                </Typography>
              </Box>
            ),
        },


        {
          field:
            "quantity",

          headerName:
            "Quantité",

          width: 110,

          align: "right",

          headerAlign:
            "right",

          renderCell:
            (params) => (
              <Typography
                sx={{
                  width: "100%",
                  textAlign: "right",

                  color: "#0f172a",

                  fontSize: "0.86rem",

                  fontWeight: 900,
                }}
              >
                {formatQuantity(
                  params.value
                )}
              </Typography>
            ),
        },


        {
          field:
            "status",

          headerName:
            "Statut",

          width: 120,

          renderCell:
            (params) =>
              getStatusChip(
                params.value
              ),
        },


        {
          field:
            "actions",

          headerName:
            "Actions",

          width: 110,

          sortable: false,

          filterable: false,

          disableColumnMenu:
            true,

          renderCell:
            (params) => (
              <Button
                size="small"
                variant="contained"
                disableElevation

                onClick={() =>
                  openEditDialog(
                    params.row.id
                  )
                }

                sx={{
                  minWidth: 82,
                  whiteSpace:
                    "nowrap",
                }}
              >
                Modifier
              </Button>
            ),
        },
      ],
      []
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",

          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiButton-root":
            {
              minHeight: 40,

              borderRadius: 2.5,

              textTransform:
                "none",

              fontWeight: 800,

              boxShadow: "none",
            },

          "& .MuiOutlinedInput-root":
            {
              borderRadius: 2.5,

              bgcolor: "#ffffff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 800,
            },

          "& .MuiPaper-root":
            {
              backgroundImage:
                "none",
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "minmax(0, 1fr) auto",
            },

            gap: 1.5,

            alignItems: "start",

            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"

              sx={{
                fontWeight: 900,

                color: "#0f172a",

                lineHeight: 1.12,

                letterSpacing:
                  "-0.025em",

                fontSize: {
                  xs: "1.65rem",
                  sm: "1.95rem",
                  md: "2.1rem",
                },
              }}
            >
              Transferts de stock
            </Typography>


            <Typography
              variant="body2"
              color="text.secondary"

              sx={{
                mt: 0.4,

                lineHeight: 1.5,

                maxWidth: 780,
              }}
            >
              Transfert des produits entre entrepôts et shops,
              avec suivi de la quantité, du statut et de la
              destination.
            </Typography>
          </Box>


          <Button
            variant="contained"
            disableElevation

            onClick={
              openAddDialog
            }

            sx={{
              minWidth: {
                xs: "100%",
                sm: 180,
              },

              justifySelf: {
                xs: "stretch",
                sm: "end",
              },
            }}
          >
            + Nouveau transfert
          </Button>
        </Box>


        {/* =================================================
            INFO
        ================================================= */}

        <Alert
          severity="info"
          variant="outlined"

          sx={{
            mb: 1.6,

            py: 0.35,

            borderRadius: 3,
          }}
        >
          Chaque transfert possède une source et une
          destination. Elles peuvent être un entrepôt ou un
          shop, mais la même source ne peut pas être utilisée
          comme destination.
        </Alert>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              md:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 1.1,

            mb: 1.6,
          }}
        >
          <MetricCard
            label="Transferts"

            value={
              stats.total
            }

            caption="Transferts affichés"

            accent="#2563eb"

            background="rgba(37, 99, 235, 0.09)"

            icon={
              <SwapHorizRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Approuvés"

            value={
              stats.approved
            }

            caption="Transferts validés"

            accent="#16a34a"

            background="rgba(22, 163, 74, 0.09)"

            icon={
              <CheckCircleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="En attente"

            value={
              stats.pending
            }

            caption="Transferts à valider"

            accent="#d97706"

            background="rgba(217, 119, 6, 0.09)"

            icon={
              <ScheduleRoundedIcon fontSize="small" />
            }
          />


          <MetricCard
            label="Quantité"

            value={
              formatQuantity(
                stats.quantity
              )
            }

            caption="Volume total transféré"

            accent="#7c3aed"

            background="rgba(124, 58, 237, 0.09)"

            icon={
              <Inventory2RoundedIcon fontSize="small" />
            }
          />
        </Box>


        {/* =================================================
            FILTERS
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            p: {
              xs: 1.15,
              sm: 1.25,
            },

            mb: 1.6,

            borderRadius: 3,

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 5px 16px rgba(15, 23, 42, 0.025)",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                lg:
                  "minmax(0, 1fr) 220px 135px",
              },

              gap: 1,

              alignItems: "center",
            }}
          >
            <TextField
              fullWidth
              size="small"

              label="Recherche"

              placeholder="Transfert, produit, source ou destination..."

              value={
                search
              }

              onChange={(
                event
              ) =>
                setSearch(
                  event.target.value
                )
              }
            />


            <TextField
              select
              fullWidth
              size="small"

              label="Statut"

              value={
                statusFilter
              }

              onChange={(
                event
              ) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="">
                Tous
              </MenuItem>

              <MenuItem value="approved">
                Approuvé
              </MenuItem>

              <MenuItem value="pending">
                En attente
              </MenuItem>
            </TextField>


            <Button
              variant="outlined"

              onClick={
                resetFilters
              }

              sx={{
                width: "100%",

                minWidth: 0,

                whiteSpace:
                  "nowrap",
              }}
            >
              Réinitialiser
            </Button>
          </Box>
        </Paper>


        {/* =================================================
            REGISTER
        ================================================= */}

        <Paper
          variant="outlined"

          sx={{
            borderRadius: 3.5,

            overflow: "hidden",

            borderColor:
              "rgba(148, 163, 184, 0.22)",

            boxShadow:
              "0 8px 24px rgba(15, 23, 42, 0.035)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.4,
                sm: 1.8,
              },

              py: 1.15,

              display: "flex",

              flexDirection: {
                xs: "column",
                sm: "row",
              },

              justifyContent:
                "space-between",

              alignItems: {
                xs: "flex-start",
                sm: "center",
              },

              gap: 0.8,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",

              bgcolor: "#ffffff",
            }}
          >
            <Box>
              <Typography
                fontWeight={900}
                color="#0f172a"
              >
                Registre des transferts
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
              >
                Source, destination, produit, quantité et statut
              </Typography>
            </Box>


            <Chip
              size="small"

              variant="outlined"

              label={`${filteredTransfers.length} transfert${
                filteredTransfers.length >
                1
                  ? "s"
                  : ""
              }`}
            />
          </Box>


          {/* =================================================
              DESKTOP
          ================================================= */}

          {!isMdDown ? (
            <Box
              sx={{
                width: "100%",

                minWidth: 0,

                overflowX: "auto",

                bgcolor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  minWidth: 1120,
                }}
              >
                <DataGrid
                  autoHeight

                  rows={
                    filteredTransfers
                  }

                  columns={
                    columns
                  }

                  loading={
                    loading
                  }

                  disableRowSelectionOnClick

                  disableColumnSelector

                  pagination

                  paginationMode="client"

                  paginationModel={
                    paginationModel
                  }

                  onPaginationModelChange={(
                    model
                  ) =>
                    setPaginationModel({
                      page:
                        model.page,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  }

                  pageSizeOptions={[
                    ROWS_PER_PAGE,
                  ]}

                  rowHeight={70}

                  columnHeaderHeight={50}

                  getRowClassName={(
                    params
                  ) =>
                    getTransferRowClass(
                      params.row.status
                    )
                  }

                  localeText={{
                    noRowsLabel:
                      "Aucun transfert de stock",

                    noResultsOverlayLabel:
                      "Aucun résultat",

                    MuiTablePagination:
                      {
                        labelRowsPerPage:
                          "",

                        labelDisplayedRows:
                          ({
                            from,
                            to,
                            count,
                          }) =>
                            `${from}–${to} sur ${count}`,
                      },
                  }}

                  sx={{
                    border: 0,

                    width: "100%",

                    color: "#334155",

                    fontSize: "0.83rem",

                    bgcolor:
                      "#ffffff",

                    "& .MuiDataGrid-columnHeaders":
                      {
                        bgcolor:
                          "#f8fafc",

                        borderBottom:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiDataGrid-columnHeader":
                      {
                        bgcolor:
                          "#f8fafc",

                        px: 1.25,

                        borderRight:
                          "1px solid rgba(226, 232, 240, 0.60)",
                      },

                    "& .MuiDataGrid-columnHeader:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeaderTitle":
                      {
                        color:
                          "#334155",

                        fontSize:
                          "0.76rem",

                        fontWeight: 900,

                        letterSpacing:
                          "0.025em",
                      },

                    "& .MuiDataGrid-columnSeparator":
                      {
                        color:
                          "rgba(148, 163, 184, 0.18)",
                      },

                    "& .MuiDataGrid-cell":
                      {
                        px: 1.25,

                        minWidth: 0,

                        display: "flex",

                        alignItems:
                          "center",

                        overflow:
                          "hidden",

                        borderBottom:
                          "1px solid #edf2f7",

                        borderRight:
                          "1px solid rgba(241, 245, 249, 0.85)",
                      },

                    "& .MuiDataGrid-cell:last-of-type":
                      {
                        borderRight:
                          "none",
                      },

                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
                      {
                        outline:
                          "none",
                      },

                    "& .stock-transfer-row":
                      {
                        bgcolor:
                          "#ffffff",

                        transition:
                          "background-color 0.16s ease, box-shadow 0.16s ease",
                      },

                    "& .stock-transfer-row--approved":
                      {
                        boxShadow:
                          "inset 4px 0 0 #22c55e",

                        bgcolor:
                          "rgba(240, 253, 244, 0.30)",
                      },

                    "& .stock-transfer-row--pending":
                      {
                        boxShadow:
                          "inset 4px 0 0 #f59e0b",

                        bgcolor:
                          "rgba(255, 251, 235, 0.36)",
                      },

                    "& .stock-transfer-row--cancelled":
                      {
                        boxShadow:
                          "inset 4px 0 0 #ef4444",

                        bgcolor:
                          "rgba(254, 242, 242, 0.30)",
                      },

                    "& .stock-transfer-row--default":
                      {
                        boxShadow:
                          "inset 4px 0 0 #cbd5e1",
                      },

                    "& .stock-transfer-row:hover":
                      {
                        bgcolor:
                          "#f8fbff !important",

                        boxShadow:
                          "inset 4px 0 0 #2563eb, 0 4px 14px rgba(15, 23, 42, 0.045)",
                      },

                    "& .MuiDataGrid-footerContainer":
                      {
                        minHeight: 52,

                        bgcolor:
                          "#fbfdff",

                        borderTop:
                          "1px solid #dfe7f1",
                      },

                    "& .MuiTablePagination-toolbar":
                      {
                        minHeight: 52,

                        px: 1.25,
                      },

                    "& .MuiTablePagination-displayedRows":
                      {
                        color:
                          "#475569",

                        fontSize:
                          "0.78rem",

                        fontWeight: 800,
                      },

                    "& .MuiTablePagination-selectLabel":
                      {
                        display: "none",
                      },

                    "& .MuiTablePagination-input":
                      {
                        display: "none",
                      },

                    "& .MuiDataGrid-virtualScroller":
                      {
                        overflowX:
                          "auto",
                      },

                    "& .MuiDataGrid-scrollbar--horizontal":
                      {
                        height:
                          "8px",
                      },
                  }}
                />
              </Box>
            </Box>
          ) : (

            /* =================================================
               MOBILE / TABLET
            ================================================= */

            <Box
              sx={{
                p: {
                  xs: 1.1,
                  sm: 1.25,
                },

                bgcolor: "#f8fafc",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Chargement des transferts...
                  </Typography>
                </Box>
              ) : paginatedMobileRows.length ===
                0 ? (
                <Box
                  sx={{
                    py: 5,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    color="text.secondary"
                  >
                    Aucun transfert trouvé.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {paginatedMobileRows.map(
                    (row) => (
                      <TransferMobileCard
                        key={
                          row.id
                        }

                        row={
                          row
                        }

                        onEdit={
                          openEditDialog
                        }
                      />
                    )
                  )}
                </Stack>
              )}


              <TablePagination
                component="div"

                count={
                  filteredTransfers.length
                }

                page={
                  paginationModel.page
                }

                onPageChange={(
                  _event,
                  newPage
                ) =>
                  setPaginationModel(
                    (previous) => ({
                      ...previous,

                      page:
                        newPage,

                      pageSize:
                        ROWS_PER_PAGE,
                    })
                  )
                }

                rowsPerPage={
                  ROWS_PER_PAGE
                }

                rowsPerPageOptions={[
                  ROWS_PER_PAGE,
                ]}

                labelRowsPerPage=""

                labelDisplayedRows={({
                  from,
                  to,
                  count,
                }) =>
                  `${from}–${to} sur ${count}`
                }

                sx={{
                  mt: 1,

                  bgcolor:
                    "#ffffff",

                  borderRadius: 2.5,

                  "& .MuiTablePagination-toolbar":
                    {
                      minHeight: 50,
                      px: 0.8,
                    },

                  "& .MuiTablePagination-selectLabel":
                    {
                      display: "none",
                    },

                  "& .MuiTablePagination-input":
                    {
                      display: "none",
                    },
                }}
              />
            </Box>
          )}
        </Paper>
      </Box>


      {/* =================================================
          DIALOG
      ================================================= */}

      <Dialog
        open={
          open
        }

        onClose={
          handleClose
        }

        fullWidth

        maxWidth="md"

        fullScreen={
          isSmDown
        }

        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            m: {
              xs: 0,
              sm: 2,
            },

            maxHeight: {
              xs: "100dvh",
              sm: "92dvh",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.3,
          }}
        >
          <Typography
            fontWeight={900}
            fontSize={20}
            color="#0f172a"
          >
            {selectedTransfer
              ? "Modifier le transfert de stock"
              : "Nouveau transfert de stock"}
          </Typography>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.3,
            }}
          >
            {selectedTransfer
              ? "Modifiez le produit, la quantité ou les emplacements du transfert."
              : "Sélectionnez le produit, la source et la destination du stock."}
          </Typography>
        </DialogTitle>


        <DialogContent dividers>
          <Stack
            spacing={2}

            sx={{
              pt: 0.5,
            }}
          >

            {/* PRODUCT */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Produit et quantité
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "minmax(0, 1.5fr) minmax(160px, 0.5fr)",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Produit"

                  name="product_id"

                  value={
                    formData.product_id
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }
                >
                  {products.map(
                    (product) => (
                      <MenuItem
                        key={
                          product.id
                        }

                        value={
                          product.id
                        }
                      >
                        {product.code} -{" "}
                        {product.name}
                      </MenuItem>
                    )
                  )}
                </TextField>


                <TextField
                  size="small"

                  label="Quantité"

                  name="quantity"

                  type="number"

                  value={
                    formData.quantity
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  required

                  disabled={
                    saving
                  }

                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                    },
                  }}
                />
              </Box>
            </Paper>


            {/* SOURCE */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Source
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Type source"

                  name="source_type"

                  value={
                    formData.source_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="warehouse">
                    Entrepôt
                  </MenuItem>

                  <MenuItem value="store">
                    Shop
                  </MenuItem>
                </TextField>


                {formData.source_type ===
                "warehouse" ? (
                  <TextField
                    select
                    size="small"

                    label="Entrepôt source"

                    name="from_warehouse_id"

                    value={
                      formData.from_warehouse_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {warehouses.map(
                      (warehouse) => (
                        <MenuItem
                          key={
                            warehouse.id
                          }

                          value={
                            warehouse.id
                          }
                        >
                          {warehouse.code} -{" "}
                          {warehouse.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                ) : (
                  <TextField
                    select
                    size="small"

                    label="Shop source"

                    name="from_store_id"

                    value={
                      formData.from_store_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {stores.map(
                      (store) => (
                        <MenuItem
                          key={
                            store.id
                          }

                          value={
                            store.id
                          }
                        >
                          {store.code} -{" "}
                          {store.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}
              </Box>
            </Paper>


            {/* DESTINATION */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Destination
              </Typography>


              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                  gap: 1.5,
                }}
              >
                <TextField
                  select
                  size="small"

                  label="Type destination"

                  name="destination_type"

                  value={
                    formData.destination_type
                  }

                  onChange={
                    handleChange
                  }

                  fullWidth

                  disabled={
                    saving
                  }
                >
                  <MenuItem value="store">
                    Shop
                  </MenuItem>

                  <MenuItem value="warehouse">
                    Entrepôt
                  </MenuItem>
                </TextField>


                {formData.destination_type ===
                "store" ? (
                  <TextField
                    select
                    size="small"

                    label="Shop destination"

                    name="to_store_id"

                    value={
                      formData.to_store_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {stores.map(
                      (store) => (
                        <MenuItem
                          key={
                            store.id
                          }

                          value={
                            store.id
                          }
                        >
                          {store.code} -{" "}
                          {store.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                ) : (
                  <TextField
                    select
                    size="small"

                    label="Entrepôt destination"

                    name="to_warehouse_id"

                    value={
                      formData.to_warehouse_id
                    }

                    onChange={
                      handleChange
                    }

                    fullWidth

                    required

                    disabled={
                      saving
                    }
                  >
                    {warehouses.map(
                      (warehouse) => (
                        <MenuItem
                          key={
                            warehouse.id
                          }

                          value={
                            warehouse.id
                          }
                        >
                          {warehouse.code} -{" "}
                          {warehouse.name}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}
              </Box>
            </Paper>


            {/* STATUS + NOTE */}

            <Paper
              variant="outlined"

              sx={{
                p: 1.5,

                borderRadius: 3,

                bgcolor: "#f8fafc",

                borderColor:
                  "rgba(148, 163, 184, 0.22)",
              }}
            >
              <Typography
                fontWeight={900}
                color="#0f172a"
                sx={{
                  mb: 1.2,
                }}
              >
                Validation
              </Typography>


              <TextField
                select
                size="small"

                label="Statut"

                name="status"

                value={
                  formData.status
                }

                onChange={
                  handleChange
                }

                fullWidth

                disabled={
                  saving
                }

                sx={{
                  mb: 1.5,
                }}
              >
                <MenuItem value="approved">
                  Approuvé
                </MenuItem>

                <MenuItem value="pending">
                  En attente
                </MenuItem>
              </TextField>


              <TextField
                label="Note"

                name="reference_note"

                value={
                  formData.reference_note
                }

                onChange={
                  handleChange
                }

                fullWidth

                multiline

                minRows={3}

                disabled={
                  saving
                }

                placeholder="Motif ou précision sur le transfert..."
              />
            </Paper>
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: {
              xs: 2,
              sm: 3,
            },

            py: 2,

            gap: 1,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }

            disabled={
              saving
            }
          >
            Annuler
          </Button>


          <Button
            variant="contained"
            disableElevation

            onClick={
              handleSave
            }

            disabled={
              saving
            }
          >
            {saving
              ? "Enregistrement..."
              : selectedTransfer
              ? "Modifier"
              : "Enregistrer"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }

        autoHideDuration={
          3500
        }

        onClose={
          handleCloseSnackbar
        }

        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={
            handleCloseSnackbar
          }

          severity={
            snackbar.severity
          }

          variant="filled"

          sx={{
            width: "100%",

            borderRadius: 3,
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default StockTransfersPage
